import { jsx } from "react/jsx-runtime";
import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  CircleOutlined as CircleOutlinedIcon
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useFormApi } from "../../../core/hooks/useFormApi.js";
function useCreateValidationSummary(props, theme) {
  const { mode = "show-errors", icons = {}, items, iconsAlignment = "top" } = props;
  const {
    valid: validIconOrColor = (theme.vars ?? theme).palette.success.main,
    neutral: neutralIconOrColor = (theme.vars ?? theme).palette.text.secondary,
    invalid: invalidIconOrColor = (theme.vars ?? theme).palette.error.main
  } = icons;
  const api = useFormApi();
  const [validationItems, setValidationItems] = useState([]);
  useEffect(() => {
    function createValidationItems() {
      return items.map((item) => {
        const { ids, field: fieldName, message } = item;
        const field = api.getField(fieldName);
        if (!field) {
          throw new Error(`[ValidationSummary] Field "${fieldName}" not found in the form`);
        }
        const fieldErrors = api.getFieldErrors(fieldName);
        const validationStatusFromErrors = fieldErrors.reduce((acc, err) => {
          return !ids || ids.includes(err.id) ? "invalid" : acc;
        }, "valid");
        let validationStatus = field.status === "pristine" ? "indeterminate" : validationStatusFromErrors;
        if (mode === "silent-errors" && validationStatus !== "valid") {
          validationStatus = "indeterminate";
        }
        const marginTop = iconsAlignment === "top" ? "1px" : 0;
        const marginBottom = iconsAlignment === "bottom" ? "2px" : 0;
        const commonSx = { fontSize: "16px", marginTop, marginBottom };
        const uid = `${String(fieldName)}_${ids?.join("_")}_${Math.random()}`;
        switch (validationStatus) {
          case "valid":
            return {
              uid,
              ids,
              text: typeof message === "string" ? message : message.valid,
              field: fieldName,
              iconElement: typeof validIconOrColor === "string" ? /* @__PURE__ */ jsx(CheckCircleIcon, { sx: { ...commonSx, color: validIconOrColor } }) : validIconOrColor,
              status: validationStatus
            };
          case "invalid":
            return {
              uid,
              ids,
              text: typeof message === "string" ? message : message.invalid,
              field: fieldName,
              iconElement: typeof invalidIconOrColor === "string" ? /* @__PURE__ */ jsx(CancelIcon, { sx: { ...commonSx, color: invalidIconOrColor } }) : invalidIconOrColor,
              status: validationStatus
            };
          case "indeterminate":
          default:
            return {
              uid,
              ids,
              text: typeof message === "string" ? message : message.neutral,
              field: fieldName,
              iconElement: typeof neutralIconOrColor === "string" ? /* @__PURE__ */ jsx(CircleOutlinedIcon, { sx: { ...commonSx, color: neutralIconOrColor } }) : neutralIconOrColor,
              status: validationStatus
            };
        }
      });
    }
    const fields = new Set(items.map((item) => item.field));
    let prevFieldsErrors = {};
    let prevFieldsStatuses = {};
    const unsubscribe = api.subscribeToForm(() => {
      const formErrors = {};
      const formStatuses = {};
      let shouldRegenerateSummary = false;
      for (const field of fields) {
        const fieldStatus = api.getField(field)?.status ?? "invalid";
        const fieldErrors = api.getFieldErrors(field);
        formErrors[field] = fieldErrors;
        formStatuses[field] = fieldStatus;
        if (fieldErrors !== prevFieldsErrors[field] || fieldStatus !== prevFieldsStatuses[field]) {
          shouldRegenerateSummary = true;
        }
      }
      prevFieldsErrors = formErrors;
      prevFieldsStatuses = formStatuses;
      if (shouldRegenerateSummary) {
        setValidationItems(createValidationItems());
      }
    });
    setValidationItems(createValidationItems());
    return () => {
      unsubscribe();
    };
  }, [api, items, iconsAlignment, invalidIconOrColor, mode, neutralIconOrColor, validIconOrColor]);
  return validationItems;
}
export {
  useCreateValidationSummary
};
//# sourceMappingURL=useCreateValidationSummary.js.map
